import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Check, X, Warning, Printer, Play } from "@phosphor-icons/react";

export default function ApplicationDetail() {
    const { id } = useParams();
    const { t } = useI18n();
    const { user } = useAuth();
    const [a, setA] = useState(null);
    const [comment, setComment] = useState("");
    const [plate, setPlate] = useState("");
    const canApprove = ["approver", "super_admin"].includes(user?.role);
    const canPrint = ["printer_operator", "super_admin"].includes(user?.role);

    const load = () => api.get(`/applications/${id}`).then((r) => setA(r.data.application));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [id]);

    const act = async (action) => {
        try {
            await api.post(`/applications/${id}/action`, { action, comment });
            setComment("");
            toast.success(action);
            load();
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("error"));
        }
    };

    const issuePlate = async () => {
        if (!plate) return;
        try {
            const { data } = await api.post("/plates/issue", { application_id: id, plate_number: plate });
            toast.success(`Plate issued: ${data.plate.plate_number}`);
            setPlate("");
            load();
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("error"));
        }
    };

    if (!a) return <div className="text-muted-foreground">{t("loading")}</div>;

    return (
        <div className="max-w-5xl space-y-6" data-testid="application-detail">
            <div>
                <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· {a.status.toUpperCase()}</div>
                <h1 className="text-3xl font-bold tracking-tight mt-1 mono">{a.reference}</h1>
                {a.fraud_flags?.length > 0 && (
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/30 text-primary rounded-sm text-xs">
                        <Warning size={14} weight="fill" />
                        Fraud flags: {a.fraud_flags.join(", ")}
                    </div>
                )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                <Card className="p-6 rounded-sm border-border">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3">{t("stepOwner")}</div>
                    <dl className="text-sm space-y-2">
                        {Object.entries(a.owner).map(([k, v]) => (
                            <div key={k} className="flex justify-between gap-4 border-b border-border/40 pb-1.5">
                                <dt className="text-muted-foreground text-xs uppercase tracking-wide">{k}</dt>
                                <dd className="mono text-end">{v || "—"}</dd>
                            </div>
                        ))}
                    </dl>
                </Card>
                <Card className="p-6 rounded-sm border-border">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3">{t("stepVehicle")}</div>
                    <dl className="text-sm space-y-2">
                        {Object.entries(a.vehicle).map(([k, v]) => (
                            <div key={k} className="flex justify-between gap-4 border-b border-border/40 pb-1.5">
                                <dt className="text-muted-foreground text-xs uppercase tracking-wide">{k}</dt>
                                <dd className="mono text-end">{v || "—"}</dd>
                            </div>
                        ))}
                    </dl>
                </Card>
            </div>

            {(canApprove && ["submitted", "under_review", "needs_info"].includes(a.status)) && (
                <Card className="p-6 rounded-sm border-border">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3">Workflow</div>
                    <Textarea placeholder={t("comment")} value={comment} onChange={(e) => setComment(e.target.value)} className="mb-3" data-testid="workflow-comment" />
                    <div className="flex flex-wrap gap-2">
                        {a.status === "submitted" && (
                            <Button variant="secondary" onClick={() => act("start_review")} data-testid="btn-start-review"><Play size={16} className="me-1" />{t("startReview")}</Button>
                        )}
                        <Button className="bg-green-700 hover:bg-green-800 text-white rounded-sm" onClick={() => act("approve")} data-testid="btn-approve"><Check size={16} weight="bold" className="me-1" />{t("approve")}</Button>
                        <Button variant="outline" onClick={() => act("needs_info")} data-testid="btn-needs-info"><Warning size={16} className="me-1" />{t("needsInfo")}</Button>
                        <Button className="bg-primary hover:bg-primary/90 rounded-sm" onClick={() => act("reject")} data-testid="btn-reject"><X size={16} weight="bold" className="me-1" />{t("reject")}</Button>
                    </div>
                </Card>
            )}

            {canPrint && a.status === "approved" && (
                <Card className="p-6 rounded-sm border-border">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3">{t("issuePlate")}</div>
                    <div className="flex gap-2 max-w-md">
                        <Input placeholder="DAM-1234-AB" value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} className="mono" data-testid="plate-number-input" />
                        <Button onClick={issuePlate} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="btn-issue-plate">
                            <Printer size={16} className="me-1" />{t("issue")}
                        </Button>
                    </div>
                </Card>
            )}

            <Card className="p-6 rounded-sm border-border">
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-3">Workflow history</div>
                <ol className="text-sm space-y-2">
                    {a.workflow?.map((w, i) => (
                        <li key={i} className="flex items-center gap-3 border-s-2 border-primary ps-3">
                            <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 bg-secondary rounded-sm">{w.action}</span>
                            <span className="mono text-xs text-muted-foreground">{new Date(w.at).toLocaleString()}</span>
                            <span className="text-xs">{w.by}</span>
                            {w.comment && <span className="text-xs text-muted-foreground">— {w.comment}</span>}
                        </li>
                    ))}
                </ol>
            </Card>
        </div>
    );
}
